import { SelectOption, SelectTranslationService } from '@agilox/ui-common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	HostListener,
	inject,
	input,
	linkedSignal,
	output,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { SelectCustomOptionDirective } from '../../directives';
import { SelectOptionGroup } from '../../interfaces';
import { CompareFnType } from '../../types/compareFn.type';

@Component({
	selector: 'ui-select-option-list',
	templateUrl: './select-option-list.component.html',
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class SelectOptionListComponent implements AfterViewInit {
	private translationService = inject(SelectTranslationService);

	options = input<SelectOption<any>[]>([]);
	selectedValues = input<SelectOption<any>[] | SelectOption<any>>([]);

	groupedOptions = linkedSignal(() => {
		return this.generateGroupedOptions(this.options());
	});

	selectableOptionsCount = computed(() => {
		// count the total number of options which are not disabled
		return this.groupedOptions().reduce((count, group) => {
			return count + group.options.filter((option) => !option.isDisabled).length;
		}, 0);
	});

	multiEnabled = input<boolean>(false);
	fieldToCompareBy = input<string>('');
	customOptionTemplate = input<SelectCustomOptionDirective | undefined>();
	groupSelectable = input<boolean>(false);
	customCompareFn = input<CompareFnType | undefined>();
	fullWidth = input<boolean>(false);
	loading = input<boolean>();
	translatedNoData = input<string>('No data available');

	selected = output<any>();
	scrollEnd = output<void>();
	focusNext = output<void>();

	optionsList = viewChild<ElementRef<HTMLUListElement> | undefined>('optionsList');

	constructor() {
		effect(() => {
			if (this.selectedValues()) {
				this.groupedOptions.set(this.generateGroupedOptions(this.options()));
			}
		});
	}

	ngAfterViewInit() {
		this.scrollToSelected();
	}

	/**
	 * Scroll to the currently selected option
	 * Can be called externally to trigger scroll on dropdown open
	 */
	scrollToSelected(): void {
		if (!Array.isArray(this.selectedValues())) {
			// wait for the DOM to render, especially important for nested dropdowns
			setTimeout(() => {
				const selectedValue = this.selectedValues() as SelectOption<any>;
				if (selectedValue && selectedValue.index !== undefined) {
					this.scrollOptionIntoView(selectedValue);
				}
			}, 100);
		}
	}

	/**
	 * Needed for keyboard navigation
	 */
	focusedOption = signal<SelectOption<any> | undefined>(undefined);

	toggleOption(option: SelectOption<any>) {
		if (option.isDisabled) {
			return;
		}
		this.selected.emit(option.value);
	}

	doGroupOptions(options: SelectOption<any>[]) {
		const groupedItemsMap: Map<string, SelectOption<any>[]> = new Map<
			string,
			SelectOption<any>[]
		>();
		options.forEach((option: SelectOption<any>) => {
			const group: string = option.group || '';
			if (!groupedItemsMap.has(group)) {
				groupedItemsMap.set(group, []);
			}
			groupedItemsMap.get(group)!.push(option);
		});
		return Array.from(groupedItemsMap.entries()).map(([name, options]) => ({
			name,
			options,
		}));
	}

	/**
	 * if there are any selected then we deselect all in the group
	 * if there are none selected then we select all in the group
	 */
	onGroupToggle(group: SelectOptionGroup<any>) {
		if (!this.groupSelectable() || group.isDisabled) return;

		const optionsToToggle: Array<any> = [];

		const hasSelected: boolean =
			group.options.map((option: SelectOption<any>) => this.compareFn(option)).filter((s) => s)
				.length > 0;
		group.options.forEach((option: SelectOption<any>) => {
			if (!option.isDisabled) {
				if (hasSelected) {
					/** Only emit the options that need to be set to false **/
					if (this.compareFn(option)) {
						optionsToToggle.push(option.value);
					}
				} else {
					/** Only emit the options that need to be set to true **/
					if (!this.compareFn(option)) {
						optionsToToggle.push(option.value);
					}
				}
			}
		});
		this.selected.emit(optionsToToggle);
	}

	setIndexes(groups: SelectOptionGroup<any>[]) {
		let index: number = 0;
		groups.forEach((group: SelectOptionGroup<any>) => {
			group.options.forEach((option: SelectOption<any>) => {
				if (!option.isDisabled) {
					option.index = index;
					index++;
				}
			});
		});
		return groups;
	}

	/**
	 * Listen for arrow up and arrow down keydown events
	 * and set the focus to the next or previous element
	 */

	@HostListener('document:keydown.arrowdown', ['$event'])
	onArrowDown($event: KeyboardEvent) {
		/**
		 * Prevent page from scrolling
		 */
		$event.stopPropagation();
		$event.preventDefault();

		if (this.groupedOptions().length === 0) {
			return;
		}

		/**
		 * If there is no focused option, set it to the first option,
		 * that is not disabled
		 */
		if (!this.focusedOption() || this.focusedOption()!.index === undefined) {
			this.focusedOption.set(this.groupedOptions()[0].options.find((option) => !option.isDisabled));
			return;
		}

		let nextIndex: number = this.focusedOption()!.index! + 1;
		if (nextIndex >= this.selectableOptionsCount()) {
			nextIndex = 0;
		}
		this.setFocusedOptionByIndex(nextIndex);
	}

	@HostListener('document:keydown.arrowup', ['$event'])
	onArrowUp($event: KeyboardEvent) {
		/**
		 * Prevent the page from scrolling
		 */
		$event.stopPropagation();
		$event.preventDefault();

		if (this.groupedOptions().length === 0) {
			return;
		}

		/**
		 * If there is no focused option, set it to the first option
		 */
		if (!this.focusedOption() || this.focusedOption()!.index === undefined) {
			this.focusedOption.set(this.groupedOptions()[0].options[0]);
			return;
		}

		let previousIndex: number = this.focusedOption()!.index! - 1;
		if (previousIndex < 0) {
			previousIndex = this.selectableOptionsCount() - 1;
		}
		this.setFocusedOptionByIndex(previousIndex);
	}

	private setFocusedOptionByIndex(index: number): void {
		let foundOption: SelectOption<any> | undefined;
		this.groupedOptions().forEach((group) => {
			group.options.forEach((option) => {
				if (option.index === index) {
					foundOption = option;
				}
			});
		});
		this.focusedOption.set(foundOption || this.focusedOption());
		this.scrollOptionIntoView(this.focusedOption()!);
	}

	/**
	 * Scroll the option into view when navigating with the keyboard
	 * @param option
	 */
	scrollOptionIntoView(option: SelectOption<any>): void {
		if (!this.optionsList()) {
			return;
		}

		const optionElement: HTMLElement | null = this.optionsList()!.nativeElement.querySelector(
			`[data-option-index="${option.index}"]`
		);
		if (!optionElement) {
			return;
		}
		optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}

	/**
	 * Listen for enter keydown event
	 * and emit the selected option
	 */
	@HostListener('document:keydown.enter')
	onKeyboardEnter() {
		if (this.focusedOption()) {
			this.selected.emit(this.focusedOption()!.value);
		}
	}

	/**
	 * Handle local keydown events on the option list
	 */
	onKeyDown(event: KeyboardEvent) {
		if (event.key === 'Tab' && !event.shiftKey) {
			event.preventDefault();
			this.focusNext.emit();
		}
	}

	compareFn(option: SelectOption<any>): boolean {
		if (this.customCompareFn()) {
			return this.customCompareFn()!(option, this.selectedValues());
		} else {
			if (Array.isArray(this.selectedValues())) {
				return (this.selectedValues() as Array<any>)!.some((value) => {
					if (this.fieldToCompareBy()) {
						return value.value[this.fieldToCompareBy()] === option.value[this.fieldToCompareBy()];
					} else {
						return value.title === option.title;
					}
				});
			}

			if (this.fieldToCompareBy()) {
				return (
					(this.selectedValues()! as SelectOption<any>).value[this.fieldToCompareBy()] ===
					option.value[this.fieldToCompareBy()]
				);
			} else {
				return (this.selectedValues()! as SelectOption<any>).title === option.title;
			}
		}
	}

	doGroupDisabling(groups: SelectOptionGroup<any>[]) {
		return groups.map((group: SelectOptionGroup<any>) => {
			return {
				...group,
				isDisabled:
					group.options.filter((option) => option.isDisabled).length === group.options.length,
			};
		});
	}

	private generateGroupedOptions(options: SelectOption<any>[]) {
		const grouped = this.doGroupOptions(options);
		const afterDisabling = this.doGroupDisabling(grouped);
		return this.setIndexes(afterDisabling);
	}

	onScroll() {
		if (this.optionsList()!) {
			const element = this.optionsList()!.nativeElement;
			const atBottom = element.scrollHeight - element.scrollTop === element.clientHeight;
			if (atBottom) {
				this.scrollEnd.emit();
			}
		}
	}

	translateGroupName(groupName: string): string {
		return this.translationService.translate(groupName);
	}
}
